import React, { useEffect, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { actions } from '../redux/ProductReducer';
import InternalContainer from '../../layout/containers/Internal';
import { Loader } from '../../../common/components';
import MedicalRulesForm from '../components/MedicalRulesForm/MedicalRulesForm';
import { getSchemeList } from '../helpers/countryMappingHelpers';

const getMedicalRules = (declarationQuestions, exclusions) => {
  if (!declarationQuestions) {
    return undefined;
  }
  const questionKeys = Object.keys(declarationQuestions);

  return questionKeys.reduce((carry, questionKey) => {
    if (declarationQuestions[questionKey]?.question_type !== 'boolean') {
      return carry;
    }

    const questionExclusions = exclusions?.[questionKey];

    if (!questionExclusions) {
      if (questionKey === 'has-psychiatric-condition') {
        carry[questionKey] = {
          content: declarationQuestions[questionKey]?.content,
          exclude_if_overridden: 'do_not_exclude',
          rule: 'Quote',
        };
      } else {
        carry[questionKey] = {
          content: declarationQuestions[questionKey]?.content,
          rule: 'Quote',
        };
      }
      return carry;
    }

    const excludeIfOverridden =
      questionExclusions?.exclude_if_overridden ?? 'do_not_exclude';

    if (excludeIfOverridden !== 'do_not_exclude') {
      carry[questionKey] = {
        content: declarationQuestions[questionKey]?.content,
        rule: 'Quote',
        exclude_if_overridden: 'exclude',
      };
    } else {
      const allowOverride = questionExclusions?.allow_override;
      carry[questionKey] = {
        content: declarationQuestions[questionKey]?.content,
        rule: 'Decline',
        allow_override: allowOverride ? 'override' : 'do_not_override',
      };
    }
    return carry;
  }, {});
};

const MedicalRulesContainer = ({
  params,
  getProduct,
  getInsurerMedicalRules,
  updateMedicalRules,
}) => {
  const { productId, insurerId } = params;
  const isSchemeGroup = ['FreeSpirit', 'Free2Travel'].includes(insurerId);
  const { product, topLevelArea } = useSelector((state) => state.productManagement);

  const currentProductId = product?.data?.id;
  useEffect(
    () => {
      if (!currentProductId || currentProductId !== productId) {
        getProduct(productId);
      }
    },
    [productId, currentProductId, getProduct],
  );

  useEffect(
    () => {
      getInsurerMedicalRules(insurerId, isSchemeGroup);
    },
    [insurerId, isSchemeGroup, getInsurerMedicalRules],
  );

  const updateMedicalValues = (formValues) => {
    const exclusions = formValues.question_exclusions;
    const questionKeys = Object.keys(exclusions);

    const rulesWithOverrides = questionKeys.reduce((carry, questionKey) => {
      const exclusion = exclusions[questionKey];

      if (exclusion.rule === 'Decline') {
        carry[questionKey] = {
          ...carry[questionKey],
          allow_override: false,
        };
      }

      if (exclusion.rule === 'Quote' && exclusion.exclude_if_overridden === 'exclude') {
        carry[questionKey] = {
          ...carry[questionKey],
          exclude_if_overridden: true,
        };
      }

      if (exclusion.rule === 'Decline' && exclusion.allow_override) {
        carry[questionKey] = {
          ...carry[questionKey],
          allow_override: exclusion.allow_override === 'override',
        };
      }
      return carry;
    }, {});
    const values = {
      meta: {
        content: formValues.content,
        parameters: {
          allow_medical_exclusion: formValues.allow_medical_exclusion,
        },
        rules: {
          medical_declaration_questions_exclusion: rulesWithOverrides,
        },
        schemes: formValues.schemes,
      },
    };
    return updateMedicalRules(values, isSchemeGroup);
  };

  const schemes = topLevelArea?.[insurerId]?.schemes;
  const parameters = topLevelArea?.[insurerId]?.parameters;

  const medicalDeclarationQuestions = parameters?.medicalDeclarationQuestions;
  const medicalDeclarationQuestionExclusions =
    parameters?.medicalDeclarationQuestionExclusions;

  const values = useMemo(
    () =>
      getMedicalRules(medicalDeclarationQuestions, medicalDeclarationQuestionExclusions),
    [medicalDeclarationQuestions, medicalDeclarationQuestionExclusions],
  );

  if (!parameters || !schemes || !values) {
    return (
      <InternalContainer title={'Medical Rules'}>
        <Loader />
      </InternalContainer>
    );
  }

  const schemeList = getSchemeList(isSchemeGroup, schemes ?? []);
  return (
    <InternalContainer title={'Medical Rules'}>
      <MedicalRulesForm
        medicalRules={values}
        allowMedicalExclusion={parameters?.allowMedicalExclusion}
        onUpdate={updateMedicalValues}
        schemesToUpdate={schemeList}
      />
    </InternalContainer>
  );
};

export default connect(
  null,
  {
    getProduct: actions.getProduct,
    getInsurerMedicalRules: actions.getInsurerMedicalRules,
    updateMedicalRules: actions.updateMedicalRules,
  },
)(MedicalRulesContainer);
